import downloadSvg from "assets/img/download.svg";
import downloadInactiveSvg from "assets/img/downloads-inactive.svg";
import cx from "classnames";
import { Card, CardList } from "components/Card";
import CSVDownload from "components/CSVDownload";
import DatePickerPopover from "components/DatePickerPopover";
import Dropdown from "components/Dropdown";
import ErrorMessage from "components/ErrorMessage";
import Icon from "components/Icon";
import Page from "components/Page";
import Table from "components/Table";
import Config from "config";
import { RouteProps } from "containers/App";
import FileUpload from "containers/FileUpload";
import ProjectFilter, { FilterOptions } from "containers/ProjectFilter";
import { PermissionsContext } from "context";
import Constants from "helpers/constants";
import { ProjectType } from "interfaces/project";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { State } from "state";
import { createAnalyticsEvent, EventTypes } from "state/analytics";
import {
  fetchMyStore2Projects,
  fetchStore2Projects,
  updateStore2Project,
} from "state/store2project";
import { buildExportFilename } from "utils/helpers";
import { sortStoreNumbersMixed } from "utils/sorters";
import { getItem } from "../helpers/storage";
import { CHAIN_ROLE, IMPERSONATIONUSER } from "../state/auth";

// type StoreResetStatusProps = PropsFromRedux & RouteProps;
type StoreResetStatusProps = PropsFromRedux & RouteProps & RouteComponentProps;

interface StoreResetStatusState {
  tableData: any[];
  selectedProjectId?: any;
  selectedStoreNumber?: string | number;
  selectedProjectName: string;
  selectedProjectType: string;
  isLoading: boolean;
  hasProjectParam: boolean;
}

const filterCardStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  height: "80px",
  alignItems: "center",
};

class StoreResetStatus extends React.Component<StoreResetStatusProps, StoreResetStatusState> {
  protected CSV_HEADERS = [
    { label: "Project ID", key: "project_id" },
    { label: "Store Number", key: "storeNumber" },
    { label: "Address", key: "address" },
    { label: "Target Reset Date", key: "target_reset_date" },
    { label: "Target Reset Time", key: "target_reset_time" },
    { label: "Actual Reset Date", key: "actual_reset_date" },
    { label: "Actual Reset Time", key: "actual_reset_time" },
  ];
  constructor(props: any) {
    super(props);

    const hasParam = !!(props as any).match?.params?.projectId;

    this.state = {
      tableData: [],
      selectedProjectName: "",
      selectedProjectType: "",
      isLoading: hasParam, // Only show loader if navigating with param
      hasProjectParam: false,
    };
  }

  componentDidMount() {
    // Read from route params, not query string
    const idFromUrl = (this.props as any).match?.params?.projectId;

    if (idFromUrl) {
      // Store as number for ProjectFilter, but keep as string for API calls
      const projectIdNum = Number(idFromUrl);

      if (!isNaN(projectIdNum)) {
        this.setState(
          {
            selectedProjectId: projectIdNum, // Store as number
            isLoading: true,
            hasProjectParam: true,
          },
          () => {            // Pass string to reloadTableData for API
            this.reloadTableData(idFromUrl);
          }
        );
      }
    }
  }

  public reloadTableData(id: string | number) {
    const user: any = this.props.user;
    const idStr = id.toString();
    const storedUser = getItem(IMPERSONATIONUSER);
    let impersonUser
    if (storedUser) {
      impersonUser = JSON.parse(storedUser);
    }
    const parsedData:any = impersonUser || user;
    const userRole = parsedData?.role;

    if (userRole === Constants.AB_SYSTEM_ADMIN) {
      return this.props.fetchStore2Projects(idStr).then(() => {
        this.filterTableData();
        this.setState({
          selectedProjectId: Number(idStr), // Ensure it's stored as number
        });
      });
    } else {
      return this.props.fetchMyStore2Projects(idStr).then(() => {
        this.filterTableData();
        this.setState({
          selectedProjectId: Number(idStr), // Ensure it's stored as number
        });
      });
    }
  }

  public projectIsSelected() {
    return this.state.selectedProjectId && this.state.selectedProjectId > -1;
  }

  public getDataToExport() {
    // Clone to avoid mutating tableData
    const data = cloneDeep(this.state.tableData);
    return data.map((row) => {
      if (row.target_reset_date) {
        const targetResetMoment = moment(row.target_reset_date);
        row.target_reset_date = targetResetMoment.local().format("MM/D/YYYY");
        row.target_reset_time = targetResetMoment.local().format("hh:mm a");
      }

      if (row.actual_reset_date) {
        const actualResetMoment = moment(row.actual_reset_date);
        row.actual_reset_date = actualResetMoment.local().format("MM/D/YYYY");
        row.actual_reset_time = actualResetMoment.local().format("hh:mm a");
      }
      return row;
    });
  }

  public filterTableData() {
    const { store2Projects = [] } = this.props;

    if (this.state.selectedStoreNumber && this.state.selectedStoreNumber.toString() !== "-1") {
      this.setState({
        tableData: store2Projects.filter(
          (row) => row.storeNumber === this.state.selectedStoreNumber
        ),
      });
    } else {
      this.setState({ tableData: store2Projects });
    }
  }

  public render() {
    const { store2Projects = [], loadingProjects } = this.props;
    const userRole = getItem(CHAIN_ROLE);
    const role = userRole ? JSON.parse(userRole).role : undefined
    const user: any = this.props.user;

    const storedUser = getItem(IMPERSONATIONUSER);
    let impersonUser
    if (storedUser) {
      impersonUser = JSON.parse(storedUser);
    }
    const parsedData:any = impersonUser || user;
    const userAdminRole = parsedData?.role;
    const isAllowed =
      role === Constants.EXTERNAL_PROJECT_ADMIN ||
      role === Constants.INTERNAL_PROJECT_ADMIN || userAdminRole === Constants.AB_SYSTEM_ADMIN;
    const storeNumDropdownOptions = [
      { label: "All Stores", value: "-1", "data-qa": "all-comments" },
    ].concat(
      store2Projects
        .map((s2p) => ({
          label: s2p.storeNumber,
          value: s2p.storeNumber,
          "data-qa": s2p.storeNumber,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );

    const data = this.getDataToExport();
    return (
      <PermissionsContext.Consumer>
        {({ set_reset }: any) => (
          <Page>
            {/* <header className="section-header" style={{flexWrap: "wrap"}}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: "wrap" }}>
              <h2  data-qa="store-reset-status-header">
                Store Reset Status
              </h2> */}
            <div hidden={!this.props.store_reset_upload.hasError}>
              <ErrorMessage errors={this.props.store_reset_upload.errors || []} />
            </div>
            <CardList>
              <Card style={filterCardStyle}>
                <div className="review-feedback-filter-container" style={{marginBottom: 0}}>
                  <p className="header">Filters</p>
                  <div className="review-feedback-store-dropdown-container">
                    <p>Store Number</p>
                    <Dropdown
                      dataQa="reset-status-store-dropdown"
                      value={
                        storeNumDropdownOptions.find(
                          (opt) => opt.value === this.state.selectedStoreNumber
                        ) || storeNumDropdownOptions[0]
                      }
                      options={storeNumDropdownOptions}
                      placeholder="Select"
                      isDisabled={
                        !this.state.selectedProjectId || this.state.selectedProjectId === -1
                      }
                      containerStyles={{
                        marginLeft: "unset",
                        fontWeight: "normal",
                        marginBottom: "10px",
                        boxShadow: "noShadow",
                        noShadow: true,
                      }}
                      onChange={(store) => {
                        this.setState(
                          {
                            selectedStoreNumber: store.value,
                          },
                          this.filterTableData
                        );
                      }}
                      isSearchable
                    />
                  </div>
                  <div style={{display: "flex", flexDirection: "column"}}>
                    <div style={{minHeight: 26}}>
                        
                    </div>
              <ProjectFilter
                key={this.state.hasProjectParam ? "locked" : "free"}
                dataQa={"select-project"}
                onChange={({ id, name, type }: FilterOptions) => {
                  this.reloadTableData(id.toString());
                  this.setState({
                    selectedProjectName: name,
                    selectedProjectType: type,
                  });
                }}
                defaultOptionLabel={"Select Project"}
                isSearchable={!this.state.hasProjectParam}
                isDisabled={this.state.hasProjectParam}
                value={
                  this.state.selectedProjectId ? Number(this.state.selectedProjectId) : undefined
                }
              />
              </div>
            </div>
            <div className="section-header" style={{margin: 0}}>
              <div className="section-header-button-group">
                <CSVDownload
                  className="store-reset-status-export-link"
                  data={data}
                  headers={this.CSV_HEADERS}
                  filename={buildExportFilename("csv", "store-reset-status")}
                  onClick={() => {
                    const { selectedProjectName, selectedProjectType } = this.state;

                    this.props.createAnalyticsEvent({
                      event: EventTypes.REPORT_EXPORT,
                      eventType: "Action",
                      projectName: selectedProjectName as string,
                      projectType: selectedProjectType as ProjectType,
                      pageName: "Store Reset Status",
                      reportRowCount: data.length,
                    });
                  }}
                >
                  <button
                    data-qa="store-reset-status-export-button"
                    className={cx("import-export-button", {
                      disabled: !this.projectIsSelected(),
                    })}
                    disabled={!this.projectIsSelected()}
                  >
                    Export
                    <Icon style={{filter: "brightness(0) saturate(100%)" }} src={this.projectIsSelected() ? downloadSvg : downloadInactiveSvg} />
                  </button>
                </CSVDownload>
                {
                // set_reset || 
                isAllowed && (
                  <FileUpload
                    // disabled={!(set_reset || isAllowed)}
                    disabled={!isAllowed}
                    prefix={"STORERESET"}
                    url={`${Config.API_SERVICE_URL}/api/store2projects`}
                    data-qa={"store-reset-status-import-button"}
                    label={"Import"}
                    dataQa={"store-resetstatus-file-upload"}
                    basicImport={true}
                    afterUploadFunction={() => {
                      this.reloadTableData(
                        this.state.selectedProjectId
                          ? this.state.selectedProjectId.toString()
                          : "-1"
                      );

                      const {
                        store_reset_upload: { content },
                      } = this.props;

                      if (content && (content as any).totalRows) {
                        this.props.createAnalyticsEvent({
                          event: EventTypes.REPORT_IMPORT,
                          eventType: "Action",
                          projectName: this.state.selectedProjectName as string,
                          projectType: this.state.selectedProjectType as ProjectType,
                          pageName: "Store Reset Status",
                          reportRowCount: (content as any).totalRows,
                        });
                      }
                    }}
                  />
                )}
                  </div>
                </div>
              </Card>
              <Card >
                <Table
                  loading={loadingProjects}
                  defaultSort={false}
                  style={{ marginTop: "unset", overflowY: "auto" }}
                  data={loadingProjects ? [] : this.state.tableData}
                  columnStyles={{
                    padding: "1px 6px",     
                    lineHeight: 1.05,        
                    fontSize: 12,
                    height: "auto",         
                    minHeight: "unset",
                    whiteSpace: "nowrap",    
                  }}
                  columns={[
                    {
                      field: "storeNumber",
                      header: "Store No.",
                      dataQa: "storeNumber",
                      sortMethod: (a: string | number, b: string | number) => sortStoreNumbersMixed(a, b),
                      formatter: (value: string, row: any) => {
                        const projectId = row.project_id || this.state.selectedProjectId;
                        const storeId = row.store_id || row.id;
                        const navigateToStore = () => {
                          if (projectId && storeId) {
                            this.props.history.push(`/projects/${projectId}/stores/${storeId}`);
                          }
                        };

                        return (
                          <div
                            style={{
                              display: "flex",
                              height: "100%",
                              alignItems: "center",
                              marginLeft: "3px",
                            }}
                          >
                            <span
                              style={{
                                cursor: projectId && storeId ? "pointer" : "default",
                                color: projectId && storeId ? "#E5B42F" : "inherit",
                                textDecoration: projectId && storeId ? "underline" : "none",
                              }}
                              onClick={projectId && storeId ? navigateToStore : undefined}
                            >
                              {value}
                            </span>
                          </div>
                        );
                      },
                    },
                    {
                      field: "address",
                      header: "Address",
                      dataQa: "address",
                      formatter: (value: string, row: any) => {
                        return (
                          <div
                            style={{
                              display: "flex",
                              height: "100%",
                              alignItems: "center",
                              marginLeft:'3px'
                            }}
                          >
                            <span>{value}</span>
                          </div>
                        );
                      },
                    },
                    {
                      field: "target_reset_date",
                      header: "Target Reset Date",
                      dataQa: "targetResetDate",
                      hideSearchIcon: true,
                      sortable: true,
                      formatter: (value: string, row: any) => {
                        const isStatusLocked = row.status === "VALIDATION IN PROGRESS";
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                            }}
                          >
                            <span style={{ padding: "5px" }} data-qa="target-date-display">
                              {value
                                ? moment.utc(value).local().format("MM/D/YYYY h:mm a")
                                : "Please select date"}
                            </span>
                            <DatePickerPopover
                              disabled={!(set_reset || isAllowed) || isStatusLocked}
                              defaultValue={value}
                              onSave={(date: moment.Moment | null) => {
                                const { selectedProjectType, selectedProjectName } = this.state;
                                const { storeName } = row;

                                this.props.createAnalyticsEvent({
                                  event: EventTypes.SET_TARGET_RESET_DATE,
                                  eventType: "Action",
                                  projectName: selectedProjectName,
                                  projectType: selectedProjectType as ProjectType,
                                  storeName: storeName,
                                  pageName: "Store Reset Status",
                                });

                                this.props
                                  .updateStore2Project(row.id, {
                                    targetResetDate: date ? moment
                                      .utc(date)
                                      .format(Constants.DATE_FORMAT__YYYY_MM_DD__HH_mm_ss) : "",
                                  })
                                  .then(() => {
                                    if (this.state.selectedProjectId) {
                                      this.reloadTableData(this.state.selectedProjectId.toString());
                                    }
                                  });
                              }}
                            />
                          </div>
                        );
                      },
                    },
                    {
                      field: "actual_reset_date",
                      header: "Actual Reset Date",
                      dataQa: "actualResetDate",
                      hideSearchIcon: true,
                      sortable: true,
                      formatter: (value: string, row: any) => {
                        const isStatusLocked = row.status === "VALIDATION IN PROGRESS";
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                            }}
                          >
                            <span style={{ padding: "5px" }} data-qa="target-date-display">
                              {value
                                ? moment.utc(value).local().format("MM/D/YYYY h:mm a")
                                : "Please select date"}
                            </span>
                            <DatePickerPopover
                              disabled={!(set_reset || isAllowed) || isStatusLocked}
                              defaultValue={value}
                              onSave={(date: moment.Moment | null) => {
                                const { selectedProjectName, selectedProjectType } = this.state;
                                const { storeName } = row;

                                this.props.createAnalyticsEvent({
                                  event: EventTypes.SET_ACTUAL_RESET_DATE,
                                  eventType: "Action",
                                  projectName: selectedProjectName,
                                  projectType: selectedProjectType as ProjectType,
                                  storeName: storeName,
                                  pageName: "Store Reset Status",
                                });

                                this.props
                                  .updateStore2Project(row.id, {
                                    actualResetDate: date ? moment
                                      .utc(date)
                                      .format(Constants.DATE_FORMAT__YYYY_MM_DD__HH_mm_ss) : "",
                                  })
                                  .then(() => {
                                    if (this.state.selectedProjectId) {
                                      this.reloadTableData(this.state.selectedProjectId.toString());
                                    }
                                  });
                              }}
                            />
                          </div>
                        );
                      },
                    },
                  ]}
                  emptyState={
                    this.projectIsSelected()
                      ? "No notes found"
                      : "Please select a project to view notes"
                  }
                />
              </Card>
            </CardList>
          </Page>
        )}
      </PermissionsContext.Consumer>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    store2Projects: state.store2Projects.content,
    loadingProjects: state.store2Projects.loading,
    store_reset_upload: state.STORERESET_file_upload,
    user: state.auth_user.content,
  }),
  { fetchMyStore2Projects, updateStore2Project, createAnalyticsEvent, fetchStore2Projects }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(StoreResetStatus);
